'use client';

import Image from 'next/image';
import Link from 'next/link';
import type { TrustBadgeInfo } from '@to/db/vendor';
import { Icon } from './Icon';

/** Icon name for each trust badge label */
const BADGE_ICON_MAP: Record<string, string> = {
  PRO: 'star',
  VERIFIED: 'verified',
  VALIDATED: 'verified_user',
  CLAIMED: 'shield',
};

export interface VendorCardVendor {
  id: number;
  companyName: string;
  logoUrl: string | null;
  city?: string | null;
  state?: string | null;
  companyPhone?: string | null;
  categories: string[];
  trustBadges: TrustBadgeInfo[];
}

export interface VendorCardProps {
  vendor: VendorCardVendor;
  /** Path prefix for vendor detail links. Default: '/vendors' */
  vendorDetailPath?: string;
  /** Show featured ribbon in top-right corner */
  showFeatured?: boolean;
  /** Show sponsored badge above the vendor info */
  showSponsored?: boolean;
}

/**
 * Reusable vendor card component for directory listings.
 * Displays company info, trust badges, and optional Featured/Sponsored indicators.
 * The entire card is a link to the vendor detail page.
 */
export function VendorCard({
  vendor,
  vendorDetailPath = '/vendors',
  showFeatured = false,
  showSponsored = false,
}: VendorCardProps) {
  const location = [vendor.city, vendor.state].filter(Boolean).join(', ');
  const primaryCategory = vendor.categories?.[0] ?? null;

  return (
    <Link
      href={`${vendorDetailPath}/${vendor.id}`}
      className="luxury-card focus-luxury group relative block overflow-hidden rounded-lg border"
      style={{
        borderColor: 'var(--brand-card-border)',
        borderRadius: 'var(--radius-card, 8px)',
        padding: '20px',
        boxShadow: 'var(--shadow-resting)',
        backgroundColor: 'var(--brand-card-bg)',
      }}
    >
      {/* Featured ribbon */}
      {showFeatured && (
        <div
          className="absolute right-0 top-0 overflow-hidden"
          style={{ width: '80px', height: '80px' }}
        >
          <div
            className="absolute flex items-center justify-center text-white"
            style={{
              width: '120px',
              top: '12px',
              right: '-32px',
              transform: 'rotate(45deg)',
              backgroundColor: 'var(--brand-primary, #014F8A)',
              fontSize: '10px',
              fontWeight: 600,
              letterSpacing: '0.08em',
              padding: '2px 0',
              textTransform: 'uppercase',
            }}
          >
            <Icon name="auto_awesome" size={10} className="mr-1" />
            Featured
          </div>
        </div>
      )}

      {/* Sponsored badge */}
      {showSponsored && (
        <div className="mb-3">
          <span
            className="inline-flex items-center gap-1 rounded-full px-2 py-0.5"
            style={{
              color: 'var(--color-sponsored, #B8860B)',
              backgroundColor: 'rgba(184, 134, 11, 0.06)',
              letterSpacing: '0.06em',
              fontSize: '10px',
              fontWeight: 600,
              textTransform: 'uppercase',
            }}
          >
            <Icon name="star" size={9} filled />
            Sponsored
          </span>
        </div>
      )}

      {/* Logo + Company info */}
      <div className="flex items-start gap-4">
        {/* Company logo */}
        <div
          className="flex h-14 w-14 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg"
          style={{
            backgroundColor: 'var(--brand-surface-subtle)',
            border: '1px solid var(--brand-card-border)',
          }}
        >
          {vendor.logoUrl ? (
            <Image
              src={vendor.logoUrl}
              alt={`${vendor.companyName} logo`}
              width={56}
              height={56}
              className="h-full w-full object-contain p-1.5"
            />
          ) : (
            <span
              className="text-xl font-semibold"
              style={{ color: 'var(--brand-input-placeholder)' }}
            >
              {vendor.companyName.charAt(0).toUpperCase()}
            </span>
          )}
        </div>

        {/* Text content */}
        <div className="min-w-0 flex-1">
          {/* Company name */}
          <h3
            className="truncate font-semibold leading-tight"
            style={{
              color: 'var(--brand-text)',
              fontSize: '16px',
              letterSpacing: '-0.01em',
            }}
          >
            {vendor.companyName}
          </h3>

          {/* Primary category */}
          {primaryCategory && (
            <p
              className="mt-1 truncate"
              style={{
                color: 'var(--brand-text-muted)',
                fontSize: '13px',
                letterSpacing: '0.01em',
              }}
            >
              {primaryCategory}
            </p>
          )}
        </div>
      </div>

      {/* Trust badges */}
      {vendor.trustBadges.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-1.5">
          {vendor.trustBadges.map((badge) => {
            const iconName = BADGE_ICON_MAP[badge.label] ?? 'verified';
            return (
              <span
                key={badge.label}
                className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5"
                style={{
                  fontSize: '11px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  backgroundColor: badge.bg,
                  color: badge.text,
                  borderColor: badge.border,
                }}
              >
                <Icon name={iconName} size={11} />
                {badge.label}
              </span>
            );
          })}
        </div>
      )}

      {/* Location + Phone — separated by a refined divider */}
      {(location || vendor.companyPhone) && (
        <div
          className="mt-4 flex flex-wrap items-center gap-3 border-t pt-3"
          style={{
            borderColor: 'var(--brand-card-border)',
          }}
        >
          {location && (
            <span
              className="inline-flex items-center gap-1.5"
              style={{
                color: 'var(--brand-input-placeholder)',
                fontSize: '12px',
                letterSpacing: '0.01em',
              }}
            >
              <Icon name="location_on" size={12} />
              {location}
            </span>
          )}
          {vendor.companyPhone && (
            <span
              className="inline-flex items-center gap-1.5"
              style={{
                color: 'var(--brand-input-placeholder)',
                fontSize: '12px',
                letterSpacing: '0.01em',
              }}
            >
              <Icon name="call" size={12} />
              {vendor.companyPhone}
            </span>
          )}
        </div>
      )}

      {/* Subtle hover indicator — arrow that slides in */}
      <div
        className="absolute bottom-4 right-4 translate-x-1 opacity-0 transition-all duration-200 group-hover:translate-x-0 group-hover:opacity-100"
        style={{ color: 'var(--brand-primary, #014F8A)' }}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M6 3l5 5-5 5" />
        </svg>
      </div>
    </Link>
  );
}
